import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
  AlertDialogAction,
} from '../../components/ui/alert-dialog';
import { Loader2, ArrowLeft, Trash2 } from 'lucide-react';
import SummaryCard from './components/SummaryCard';
import ExportPanel from './components/ExportPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function TriageSummary() {
  const { callId } = useParams();
  const navigate = useNavigate();
  const [call, setCall] = useState(null);
  const [synthesis, setSynthesis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [synthesizing, setSynthesizing] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const startedRef = useRef(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/triage/calls/${callId}`);
      toast.success('Call deleted');
      navigate('/triage');
    } catch (e) {
      toast.error('Failed to delete call');
      setDeleting(false);
    }
  };

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/triage/calls/${callId}`);
        const c = res.data.call;
        setCall(c);
        const existing = c.synthesis;
        if (existing && existing.generated_at) {
          setSynthesis(existing);
        } else {
          // Trigger synthesis on first load.
          setSynthesizing(true);
          try {
            const synthRes = await axios.post(`${API}/triage/calls/${callId}/synthesize`, { call_id: callId });
            setSynthesis(synthRes.data.synthesis);
          } catch (e) {
            toast.error('Synthesis failed — you can still export raw notes');
          } finally {
            setSynthesizing(false);
          }
        }
      } catch (e) {
        toast.error('Failed to load call');
        navigate('/triage');
      } finally {
        setLoading(false);
      }
    })();
  }, [callId, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#0a0a0a] text-white/40">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0a] via-[#1a1a2e] to-[#0a0a0a] p-6">
      <div className="flex items-center justify-between mb-4">
        <Button variant="ghost" onClick={() => navigate('/triage')} className="text-white/60">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> All Calls
        </Button>
        <Button
          variant="ghost"
          onClick={() => setConfirmDeleteOpen(true)}
          className="text-white/40 hover:text-red-400 hover:bg-red-500/10">
          <Trash2 className="w-4 h-4 mr-1.5" /> Delete
        </Button>
      </div>

      <AlertDialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <AlertDialogContent className="glass-morphism border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete this call?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/50">
              {call?.prospect_name ? `"${call.prospect_name}" — ` : ''}This can&apos;t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={deleting}
              className="bg-white/5 border-white/10 text-white/70 hover:bg-white/10 hover:text-white">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleDelete(); }}
              disabled={deleting}
              className="bg-red-500/90 hover:bg-red-500 text-white">
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <h1 className="text-2xl font-semibold text-white mb-1">
        {call?.prospect_name || 'Unknown'} <span className="text-white/40 text-base">· {call?.trade}</span>
      </h1>
      <p className="text-white/50 text-sm mb-6">Call summary &amp; export</p>

      <div className="max-w-4xl">
        <Tabs defaultValue="summary">
          <TabsList className="bg-white/5 border border-white/10">
            <TabsTrigger value="summary" className="data-[state=active]:bg-[#00d4aa]/20 data-[state=active]:text-[#00d4aa]">
              Summary
            </TabsTrigger>
            <TabsTrigger value="export" className="data-[state=active]:bg-[#00d4aa]/20 data-[state=active]:text-[#00d4aa]">
              Export
            </TabsTrigger>
          </TabsList>

          <TabsContent value="summary" className="mt-4">
            {synthesizing ? (
              <div className="flex items-center gap-2 text-white/50 text-sm py-10">
                <Loader2 className="w-5 h-5 animate-spin" /> Generating call synthesis...
              </div>
            ) : (
              <SummaryCard synthesis={synthesis} />
            )}
          </TabsContent>

          <TabsContent value="export" className="mt-4">
            <ExportPanel callId={callId} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

export default TriageSummary;
